<template>
  <div class="article-slot-header__wrapper">
    <a-visibility
      v-if="isCategoryAndSponsoredAndPinSectionVisible"
      show
      :on="[$breakpoint.mobile, $breakpoint.tablet]"
    >
      <div class="article-slot-header__category-pin">
        <div class="article-slot-header__category-sponsored-wrapper">
          <div
            v-if="isSponsored"
            class="article-slot-header__category--sponsored text-medium bold"
          >
            Sponsored
          </div>

          <a-category-label-link
            :category="category"
            :hide-category="hideCategory"
          />
        </div>

        <a-icon
          v-if="isPinVisible"
          :icon="$options.consts.ICON.PIN_PURPLE"
          :width="16"
          :height="16"
          :original="true"
          class="article-slot-header__pin"
        />
      </div>
    </a-visibility>

    <div :class="dynamicWrapperClass">
      <!-- floated on mobile, flex-positioned on tablet+ -->
      <a-visibility
        v-if="showFeaturedMedia"
        show
        :on="breakpointsToShowFeaturedMediaOn"
      >
        <article-featured-media
          :slug="slug"
          :is-migrated="isMigrated"
          :category="category"
          :featured-image="featuredImage"
          :thumbnail-image="thumbnailImage"
          :featured-video="featuredVideo"
          :viewport-optimization="imageViewportOptimization"
          :srcset-location="imageSrcsetLocation"
          class="article-slot-header__media"
        />
      </a-visibility>

      <div class="article-slot-header__title-tldr-wrapper">
        <a-visibility
          v-if="isCategoryAndSponsoredAndPinSectionVisible"
          hide
          :on="[$breakpoint.mobile, $breakpoint.tablet]"
        >
          <div class="article-slot-header__category-pin">
            <div class="article-slot-header__category-sponsored-wrapper">
              <div
                v-if="isSponsored"
                class="article-slot-header__category--sponsored text-medium bold"
              >
                Sponsored
              </div>

              <a-category-label-link
                :category="category"
                :hide-category="hideCategory"
              />
            </div>

            <a-icon
              v-if="isPinVisible"
              :icon="$options.consts.ICON.PIN_PURPLE"
              :width="16"
              :height="16"
              :original="true"
              class="article-slot-header__pin"
            />
          </div>
        </a-visibility>

        <a-link
          :to="linkToArticle"
          :trailing-slash="!isMigrated"
          class="article-slot-header__link"
        >
          <component :is="titleHtmlTag" class="article-slot-header__title">
            {{ title }}
          </component>

          <a-tldr
            :tldr="tldr"
            :font-bold="false"
            class="article-slot-header__tldr"
          />
        </a-link>

        <a-visibility show :on="breakpointsToShowExpandableContentOn">
          <collapse-transition>
            <div
              v-if="isContentExpanded"
              class="article-slot-header__expanded-content-wrapper text-body"
            >
              <div
                v-html="expandedContent"
                class="article-slot-header__expanded-content article-body-normal clearfix"
              />

              <a-link
                :to="linkToArticle"
                class="article-slot-header__expanded-read-all-text text-medium bold"
              >
                Read full article
              </a-link>
            </div>
          </collapse-transition>
        </a-visibility>

        <a-visibility hide :on="[$breakpoint.mobile, $breakpoint.tablet]">
          <article-slot-info
            :category="category"
            :author="author"
            :published-on="publishedOn"
            :sponsorship-company="sponsorshipCompany"
            :link-to-article="linkToArticle"
            :expanded-content="expandedContent"
            :show-expandable-content="showExpandableContent"
            :is-content-expanded="isContentExpanded"
            :breakpoints-to-show-expandable-content-on="
              breakpointsToShowExpandableContentOn
            "
            @expanded-changed="handledExpandedChanged"
          />
        </a-visibility>
      </div>
    </div>
  </div>
</template>

<script>
import ATldr from 'shared/ATldr/index.vue'
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import ArticleFeaturedMedia from 'shared/AArticleSlot/ArticleFeaturedMedia/index.vue'
import mixins from '@/utils/mixins'
import AIcon, { ICON } from 'shared/AIcon/index.vue'
import ArticleSlotInfo from 'shared/AArticleSlot/ArticleSlotInfo'
import scss from '@/utils/scss'
import ACategoryLabelLink from 'shared/ACategoryLabelLink'
import { ARTICLE_SLOT_STYLE } from 'shared/AArticleSlot/enums'

export default {
  name: 'ArticleSlotHeader',
  mixins: [mixins.urlGenerators],
  components: {
    ArticleFeaturedMedia,
    ATldr,
    AIcon,
    ArticleSlotInfo,
    ACategoryLabelLink
  },
  consts: {
    ICON
  },
  props: {
    linkToArticle: propValidator([PROP_TYPES.STRING]),
    title: propValidator([PROP_TYPES.STRING]),
    isMigrated: propValidator([PROP_TYPES.BOOLEAN], false, false),
    tldr: propValidator([PROP_TYPES.STRING], false),
    titleHtmlTag: propValidator(
      [PROP_TYPES.STRING],
      false,
      HTML_TAG.HEADING_H3
    ),
    slug: propValidator([PROP_TYPES.STRING]),
    category: propValidator([PROP_TYPES.OBJECT], false),
    featuredImage: propValidator([PROP_TYPES.OBJECT], false),
    thumbnailImage: propValidator([PROP_TYPES.OBJECT], false),
    featuredVideo: propValidator([PROP_TYPES.OBJECT], false),
    showFeaturedMedia: propValidator([PROP_TYPES.BOOLEAN], false, true),
    breakpointsToShowFeaturedMediaOn: propValidator([PROP_TYPES.ARRAY]),
    imageViewportOptimization: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      false
    ),
    imageSrcsetLocation: propValidator([PROP_TYPES.STRING], false, null),
    hideCategory: propValidator([PROP_TYPES.BOOLEAN], false, false),
    expandedContent: propValidator([PROP_TYPES.STRING], false),
    isContentExpanded: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isPinned: propValidator([PROP_TYPES.BOOLEAN], false, false),
    publishedOn: propValidator([PROP_TYPES.STRING]),
    author: propValidator([PROP_TYPES.OBJECT], false),
    sponsorshipCompany: propValidator([PROP_TYPES.OBJECT], false),
    showExpandableContent: propValidator([PROP_TYPES.BOOLEAN], false, true),
    breakpointsToShowExpandableContentOn: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => Object.values(scss.breakpoints)
    ),
    showPin: propValidator([PROP_TYPES.BOOLEAN], false, false),
    articleSlotStyle: propValidator([PROP_TYPES.STRING], false)
  },
  methods: {
    handledExpandedChanged(isExpanded) {
      this.$emit('expanded-changed', isExpanded)
    }
  },
  computed: {
    isSponsored() {
      return !!this.sponsorshipCompany
    },
    isCategoryAndSponsoredAndPinSectionVisible() {
      return !this.hideCategory || this.isPinVisible || !this.sponsorshipCompany
    },
    isPinVisible() {
      return this.isPinned && this.showPin
    },
    dynamicWrapperClass() {
      return {
        'article-slot-header__item':
          this.articleSlotStyle === ARTICLE_SLOT_STYLE.LARGE,
        'article-slot-header__item-small':
          this.articleSlotStyle === ARTICLE_SLOT_STYLE.SMALL
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/local/styles/_article-body.scss';
.article-slot-header__wrapper {
  .article-slot-header__category-pin {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .article-slot-header__category-sponsored-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .article-slot-header__category--sponsored {
    padding: 4px 10px;
    color: $c--gray;
    background-color: $c--gray-soft;
    border-radius: 6px;

    &:hover {
      color: $c--black;
    }
  }

  .article-slot-header__pin {
    margin-left: 8px;
  }

  .article-slot-header__item,
  .article-slot-header__item-small {
    $display: (
      $above-desktop-xl: flex,
      $tablet: block
    );

    $flex-direction: (
      $above-desktop-xl: row-reverse
    );

    $justify-content: (
      $above-desktop-xl: space-between
    );

    @include setStyleByBreakpoint('display', $display);
    @include setStyleByBreakpoint('flex-direction', $flex-direction);
    @include setStyleByBreakpoint('justify-content', $justify-content);

    .article-slot-header__title-tldr-wrapper {
      $flex: (
        $above-desktop-xl: 1
      );

      @include setStyleByBreakpoint('flex', $flex);
    }

    .article-slot-header__title {
      font-weight: 500;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      @include subtitle1;

      @include tablet {
        @include subtitle2;
      }

      @include mobile {
        @include subtitle4;
      }
    }

    .article-slot-header__tldr {
      $margin-top: (
        $above-desktop-xl: 16px,
        $mobile: 8px
      );

      @include setStyleByBreakpoint('margin-top', $margin-top);
    }

    .article-slot-header__media {
      border-radius: 2px;
      flex-shrink: 0;

      // float only on tablet & mobile
      $float: (
        $tablet: right
      );

      $width: (
        $above-desktop-xl: 250px,
        $tablet: 120px,
        $mobile: 100px
      );

      $margin: (
        $above-desktop-xl: 0 0 0 20px,
        $tablet: 0 0 0 20px,
        $mobile: 0 0 18px 8px
      );

      @include setStyleByBreakpoint('margin', $margin);
      @include setStyleByBreakpoint('float', $float);
      @include setStyleByBreakpoint('width', $width);

      /deep/ .article-featured-media__media {
        border-radius: 4px;

        @include mobile {
          border-radius: 2px;
        }
      }
    }

    .article-slot-header__expanded-content-wrapper {
      color: $c--gray-dark-2;
      padding-top: 15px;

      /deep/ * {
        color: $c--gray-dark-2;
      }

      // Avoid breaking the layout by the figures inside article's
      // expandable content
      /deep/ figure {
        width: 100%;
      }
    }

    .article-slot-header__expanded-read-all-text {
      display: flex;
      color: $c--mid-purple;
      margin-top: 18px;

      &:hover {
        color: $c--main;
        text-decoration: underline;
      }

      $line-height: (
        $above-desktop-xl: 22px,
        $tablet: 20px
      );

      @include setStyleByBreakpoint('line-height', $line-height);
    }
  }

  .article-slot-header__item-small {
    .article-slot-header__title {
      @include subtitle5;

      @include tablet {
        @include subtitle2;
      }

      @include mobile {
        @include subtitle4;
      }
    }

    .article-slot-header__category-pin {
      margin-bottom: 8px;
    }

    .article-info__wrapper {
      padding-top: 8px;
    }

    .article-slot-header__category--sponsored,
    /deep/ .category-label-link,
    /deep/ .article-info__publisher,
    /deep/ .article-info-date__text {
      font-size: 12px;
      line-height: 16px;
    }
  }
}
</style>
